import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	getAdminProducts,
	deleteProductById,
	updateProductById,
	uploadProductImage,
	getCategories,
} from "../../services/adminService";

// ── Status badge ──────────────────────────────────────────────────
const StatusBadge = ({ available, stock }) => {
	if (!available) {
		return (
			<span style={badge("red")}>
				<Dot color="#A32D2D" /> Inactif
			</span>
		);
	}

	if (stock === 0) {
		return (
			<span style={badge("amber")}>
				<Dot color="#854F0B" /> Épuisé
			</span>
		);
	}

	return (
		<span style={badge("green")}>
			<Dot color="#3B6D11" /> Actif
		</span>
	);
};

const Dot = ({ color }) => (
	<span
		style={{
			display: "inline-block",
			width: 6,
			height: 6,
			borderRadius: "50%",
			background: color,
			marginRight: 5,
			flexShrink: 0,
		}}
	/>
);

const badge = (color) => {
	const map = {
		green: {
			bg: "#EAF3DE",
			text: "#3B6D11",
		},
		amber: {
			bg: "#FAEEDA",
			text: "#854F0B",
		},
		red: {
			bg: "#FCEBEB",
			text: "#A32D2D",
		},
	};

	return {
		display: "inline-flex",
		alignItems: "center",
		padding: "3px 10px",
		borderRadius: 20,
		fontSize: 12,
		fontWeight: 500,
		background: map[color].bg,
		color: map[color].text,
		whiteSpace: "nowrap",
	};
};

// ── Image thumbnail ───────────────────────────────────────────────
const ProductThumb = ({ src, alt }) =>
	src ? (
		<img
			src={src}
			alt={alt}
			style={{
				width: 40,
				height: 40,
				borderRadius: 8,
				objectFit: "cover",
				border: "0.5px solid #E6E1D6",
				display: "block",
			}}
		/>
	) : (
		<div
			style={{
				width: 40,
				height: 40,
				borderRadius: 8,
				background: "#F5F3EE",
				border: "0.5px solid #E6E1D6",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				color: "#B4B2A9",
				fontSize: 18,
			}}
		>
			☕
		</div>
	);

// ── Form field ────────────────────────────────────────────────────
const Field = ({ label, children, required }) => (
	<div
		style={{
			display: "flex",
			flexDirection: "column",
			gap: 4,
		}}
	>
		<label
			style={{
				fontSize: 12,
				fontWeight: 500,
				color: "#6B6B6B",
				letterSpacing: "0.04em",
			}}
		>
			{label}
			{required && (
				<span
					style={{
						color: "#E24B4A",
						marginLeft: 2,
					}}
				>
					*
				</span>
			)}
		</label>

		{children}
	</div>
);

const inputStyle = {
	border: "0.5px solid #D3D1C7",
	borderRadius: 10,
	padding: "9px 12px",
	fontSize: 14,
	color: "#2A2A2A",
	background: "#FFFCF8",
	outline: "none",
	width: "100%",
	boxSizing: "border-box",
	fontFamily: "inherit",
};

// ── Edit modal ────────────────────────────────────────────────────
const ProductEditModal = ({
	form,
	onChange,
	onSubmit,
	onClose,
	submitting,
	imagePreview,
	onImageChange,
	categories,
}) => (
	<div
		style={{
			position: "fixed",
			inset: 0,
			zIndex: 50,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			background: "rgba(0,0,0,0.45)",
			backdropFilter: "blur(3px)",
			padding: "24px 16px",
		}}
		onClick={onClose}
	>
		<div
			style={{
				width: "100%",
				maxWidth: 680,
				maxHeight: "90vh",
				overflowY: "auto",
				background: "#FFFCF8",
				borderRadius: 20,
				border: "0.5px solid #E6E1D6",
				boxShadow: "0 24px 64px rgba(0,0,0,0.16)",
				display: "flex",
				flexDirection: "column",
			}}
			onClick={(e) => e.stopPropagation()}
		>
			{/* Header */}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					padding: "18px 24px",
					borderBottom: "0.5px solid #E6E1D6",
					position: "sticky",
					top: 0,
					background: "#FFFCF8",
					zIndex: 10,
					borderRadius: "20px 20px 0 0",
				}}
			>
				<div>
					<h2
						style={{
							margin: 0,
							fontSize: 18,
							fontWeight: 600,
							color: "#2A2A2A",
						}}
					>
						Modifier le produit
					</h2>

					<p
						style={{
							margin: "2px 0 0",
							fontSize: 13,
							color: "#8A857D",
						}}
					>
						Modifiez les informations du produit
					</p>
				</div>

				<button
					type="button"
					onClick={onClose}
					disabled={submitting}
					style={{
						width: 36,
						height: 36,
						borderRadius: "50%",
						border: "0.5px solid #E6E1D6",
						background: "transparent",
						color: "#6B6B6B",
						cursor: "pointer",
						fontSize: 18,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexShrink: 0,
					}}
				>
					✕
				</button>
			</div>

			{/* Body */}
			<form
				onSubmit={onSubmit}
				style={{
					padding: "24px",
					display: "flex",
					flexDirection: "column",
					gap: 20,
				}}
			>
				{/* Image + informations principales */}
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "160px minmax(0,1fr)",
						gap: 20,
					}}
				>
					{/* Image */}
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: 10,
						}}
					>
						<label
							style={{
								fontSize: 12,
								fontWeight: 500,
								color: "#6B6B6B",
								letterSpacing: "0.04em",
							}}
						>
							Image
						</label>

						{imagePreview ? (
							<img
								src={imagePreview}
								alt="Aperçu"
								style={{
									width: "100%",
									height: 160,
									objectFit: "cover",
									borderRadius: 12,
									border: "0.5px solid #E6E1D6",
									display: "block",
								}}
							/>
						) : (
							<div
								style={{
									height: 160,
									border: "1.5px dashed #D3D1C7",
									borderRadius: 12,
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									gap: 6,
									color: "#B4B2A9",
									fontSize: 13,
								}}
							>
								<span style={{ fontSize: 28 }}>📷</span>
								<span>Aucune image</span>
							</div>
						)}

						<input
							type="file"
							accept="image/*"
							onChange={onImageChange}
							style={{
								fontSize: 12,
								color: "#6B6B6B",
							}}
						/>

						<p
							style={{
								margin: 0,
								fontSize: 11,
								color: "#B4B2A9",
							}}
						>
							JPG, PNG — max 5 Mo
						</p>
					</div>

					{/* Informations */}
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: 12,
						}}
					>
						<Field label="Nom du produit" required>
							<input
								name="name"
								value={form.name}
								onChange={onChange}
								style={inputStyle}
								required
							/>
						</Field>

						<Field label="Slug (URL)">
							<input
								name="slug"
								value={form.slug}
								onChange={onChange}
								style={{
									...inputStyle,
									fontFamily: "monospace",
									fontSize: 13,
								}}
							/>
						</Field>

						<Field label="Catégorie" required>
							<select
								name="category_id"
								value={form.category_id}
								onChange={onChange}
								style={inputStyle}
								required
							>
								<option value="">Sélectionner une catégorie</option>

								{categories.map((category) => (
									<option key={category.id} value={category.id}>
										{category.name}
									</option>
								))}
							</select>
						</Field>
					</div>
				</div>

				{/* Description courte */}
				<Field label="Description courte">
					<input
						name="short_description"
						value={form.short_description || ""}
						onChange={onChange}
						placeholder="Résumé affiché sur les cartes produit"
						style={inputStyle}
					/>
				</Field>

				{/* Description complète */}
				<Field label="Description complète">
					<textarea
						name="description"
						value={form.description || ""}
						onChange={onChange}
						placeholder="Description détaillée du produit..."
						rows={4}
						style={{
							...inputStyle,
							resize: "vertical",
							lineHeight: 1.6,
						}}
					/>
				</Field>

				{/* Prix + stock */}
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "1fr 1fr",
						gap: 12,
					}}
				>
					<Field label="Prix (CHF)" required>
						<input
							name="price"
							type="number"
							step="0.05"
							min="0"
							value={form.price}
							onChange={onChange}
							placeholder="0.00"
							style={inputStyle}
							required
						/>
					</Field>

					<Field label="Stock">
						<input
							name="stock"
							type="number"
							min="0"
							value={form.stock}
							onChange={onChange}
							placeholder="100"
							style={inputStyle}
						/>
					</Field>
				</div>

				{/* Statuts */}
				<div
					style={{
						display: "flex",
						gap: 24,
						padding: "12px 16px",
						background: "#F5F3EE",
						borderRadius: 10,
					}}
				>
					<label
						style={{
							display: "flex",
							alignItems: "center",
							gap: 8,
							fontSize: 14,
							color: "#2A2A2A",
							cursor: "pointer",
						}}
					>
						<input
							type="checkbox"
							name="available"
							checked={form.available}
							onChange={onChange}
						/>
						Produit actif
					</label>

					<label
						style={{
							display: "flex",
							alignItems: "center",
							gap: 8,
							fontSize: 14,
							color: "#2A2A2A",
							cursor: "pointer",
						}}
					>
						<input
							type="checkbox"
							name="featured"
							checked={form.featured}
							onChange={onChange}
						/>
						Mise en avant
					</label>
				</div>

				{/* Footer */}
				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 10,
						paddingTop: 8,
						borderTop: "0.5px solid #E6E1D6",
					}}
				>
					<button
						type="button"
						onClick={onClose}
						disabled={submitting}
						style={{
							padding: "9px 20px",
							borderRadius: 10,
							border: "0.5px solid #D3D1C7",
							background: "transparent",
							color: "#6B6B6B",
							fontSize: 14,
							cursor: "pointer",
							fontFamily: "inherit",
						}}
					>
						Annuler
					</button>

					<button
						type="submit"
						disabled={submitting}
						style={{
							padding: "9px 24px",
							borderRadius: 10,
							border: "none",
							background: "#2A2A2A",
							color: "#FFFCF8",
							fontSize: 14,
							fontWeight: 600,
							cursor: submitting ? "not-allowed" : "pointer",
							opacity: submitting ? 0.6 : 1,
							fontFamily: "inherit",
						}}
					>
						{submitting ? "Enregistrement..." : "Enregistrer"}
					</button>
				</div>
			</form>
		</div>
	</div>
);

// ── Main page ─────────────────────────────────────────────────────
const AdminProductsPage = () => {
	const navigate = useNavigate();

	const [products, setProducts] = useState([]);
	const [categories, setCategories] = useState([]);

	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [deleting, setDeleting] = useState(null);

	const [editOpen, setEditOpen] = useState(false);
	const [form, setForm] = useState(null);

	const [editId, setEditId] = useState(null);
	const [imageFile, setImageFile] = useState(null);
	const [preview, setPreview] = useState("");

	// ── Load products and categories ───────────────────────────────
	useEffect(() => {
		load();
	}, []);

	const load = async () => {
		try {
			setLoading(true);

			const [productsData, categoriesData] = await Promise.all([
				getAdminProducts(),
				getCategories(),
			]);

			setProducts(productsData || []);
			setCategories(categoriesData || []);
		} catch (error) {
			console.error(
				"Erreur chargement produits admin:",
				error?.message || error,
			);
		} finally {
			setLoading(false);
		}
	};

	// ── Form change ────────────────────────────────────────────────
	const onChange = (e) => {
		const { name, value, type, checked } = e.target;

		setForm((prev) => ({
			...prev,
			[name]: type === "checkbox" ? checked : value,
		}));
	};

	// ── Image selection ────────────────────────────────────────────
	const onImage = (e) => {
		const file = e.target.files?.[0];

		if (!file) return;

		if (file.size > 5 * 1024 * 1024) {
			alert("L'image ne doit pas dépasser 5 Mo.");
			return;
		}

		setImageFile(file);

		const objectUrl = URL.createObjectURL(file);
		setPreview(objectUrl);
	};

	// ── Save existing product ──────────────────────────────────────
	const save = async (e) => {
		e.preventDefault();

		if (!editId || !form) return;

		setSaving(true);

		try {
			const payload = {
				name: form.name,
				slug: form.slug,
				category_id: form.category_id || null,
				short_description: form.short_description || null,
				description: form.description || null,
				price: Number(form.price || 0),
				stock:
					form.stock === "" || form.stock === null ? null : Number(form.stock),
				available: Boolean(form.available),
				featured: Boolean(form.featured),
			};

			const product = await updateProductById(editId, payload);

			// Upload d'une nouvelle image uniquement si l'admin
			// en a sélectionné une.
			if (imageFile && product?.id) {
				const url = await uploadProductImage(imageFile, product.id);

				await updateProductById(product.id, {
					image_url: url,
				});
			}

			// Recharger les données.
			await load();

			// Invalider le cache du catalogue.
			sessionStorage.removeItem("menu_products");
			sessionStorage.removeItem("menu_products_at");
			sessionStorage.removeItem("featured_products");
			sessionStorage.removeItem("featured_products_at");

			close();
		} catch (error) {
			console.error("Erreur modification produit:", error?.message || error);

			alert(
				error?.message ||
					"Une erreur est survenue lors de la modification du produit.",
			);
		} finally {
			setSaving(false);
		}
	};

	// ── Delete product ─────────────────────────────────────────────
	const remove = async (id) => {
		if (!confirm("Supprimer ce produit définitivement ?")) {
			return;
		}

		setDeleting(id);

		try {
			await deleteProductById(id);

			setProducts((current) => current.filter((product) => product.id !== id));

			// Invalider le cache du catalogue.
			sessionStorage.removeItem("menu_products");
			sessionStorage.removeItem("menu_products_at");
			sessionStorage.removeItem("featured_products");
			sessionStorage.removeItem("featured_products_at");
		} catch (error) {
			console.error("Erreur suppression produit:", error?.message || error);

			alert(
				error?.message ||
					"Une erreur est survenue lors de la suppression du produit.",
			);
		} finally {
			setDeleting(null);
		}
	};

	// ── Open edit modal ────────────────────────────────────────────
	const openEdit = (product) => {
		setEditId(product.id);

		setForm({
			name: product.name || "",
			slug: product.slug || "",
			category_id: product.category_id || "",
			short_description: product.short_description || "",
			description: product.description || "",
			price: product.price ?? "",
			stock: product.stock ?? "",
			available: product.available ?? true,
			featured: product.featured ?? false,
		});

		setPreview(product.image_url || "");
		setImageFile(null);
		setEditOpen(true);
	};

	// ── Close edit modal ───────────────────────────────────────────
	const close = () => {
		if (preview && preview.startsWith("blob:")) {
			URL.revokeObjectURL(preview);
		}

		setEditOpen(false);
		setEditId(null);
		setForm(null);
		setImageFile(null);
		setPreview("");
	};

	// ── Format price ───────────────────────────────────────────────
	const formatPrice = (price) =>
		new Intl.NumberFormat("fr-CH", {
			style: "currency",
			currency: "CHF",
		}).format(Number(price || 0));

	return (
		<div
			style={{
				maxWidth: 1280,
				margin: "0 auto",
				padding: "128px 24px 48px",
			}}
		>
			{/* Header */}
			<div
				style={{
					display: "flex",
					alignItems: "flex-start",
					justifyContent: "space-between",
					marginBottom: 32,
					gap: 20,
				}}
			>
				<div>
					<h1
						style={{
							margin: 0,
							fontSize: 28,
							fontWeight: 700,
							color: "#2A2A2A",
						}}
					>
						Produits
					</h1>

					<p
						style={{
							margin: "6px 0 0",
							fontSize: 15,
							color: "#8A857D",
						}}
					>
						{products.length} produit
						{products.length !== 1 ? "s" : ""} dans le catalogue
					</p>
				</div>

				{/* Navigation vers la page de création */}
				<button
					type="button"
					onClick={() => navigate("/admin/products/new")}
					style={{
						display: "inline-flex",
						alignItems: "center",
						gap: 8,
						padding: "10px 20px",
						background: "#2A2A2A",
						color: "#FFFCF8",
						border: "none",
						borderRadius: 12,
						fontSize: 14,
						fontWeight: 600,
						cursor: "pointer",
						fontFamily: "inherit",
						flexShrink: 0,
					}}
				>
					<span
						style={{
							fontSize: 18,
							lineHeight: 1,
						}}
					>
						+
					</span>
					Ajouter un produit
				</button>
			</div>

			{/* Loading */}
			{loading ? (
				<div
					style={{
						padding: "48px 0",
						textAlign: "center",
						color: "#8A857D",
						fontSize: 15,
					}}
				>
					Chargement des produits...
				</div>
			) : products.length === 0 ? (
				/* Empty state */
				<div
					style={{
						padding: "64px 24px",
						textAlign: "center",
						background: "#FFFCF8",
						border: "0.5px solid #E6E1D6",
						borderRadius: 16,
						color: "#8A857D",
					}}
				>
					<p
						style={{
							fontSize: 32,
							margin: "0 0 12px",
						}}
					>
						☕
					</p>

					<p
						style={{
							margin: 0,
							fontSize: 16,
							fontWeight: 500,
							color: "#2A2A2A",
						}}
					>
						Aucun produit
					</p>

					<p
						style={{
							margin: "6px 0 0",
							fontSize: 14,
						}}
					>
						Commencez par ajouter votre premier produit.
					</p>
				</div>
			) : (
				/* Products table */
				<div
					style={{
						background: "#FFFCF8",
						border: "0.5px solid #E6E1D6",
						borderRadius: 16,
						overflow: "hidden",
					}}
				>
					<div
						style={{
							overflowX: "auto",
						}}
					>
						<table
							style={{
								width: "100%",
								minWidth: 850,
								borderCollapse: "collapse",
								tableLayout: "fixed",
							}}
						>
							<colgroup>
								<col style={{ width: 56 }} />
								<col style={{ width: "28%" }} />
								<col style={{ width: "16%" }} />
								<col style={{ width: "11%" }} />
								<col style={{ width: "9%" }} />
								<col style={{ width: "14%" }} />
								<col style={{ width: 100 }} />
							</colgroup>

							<thead>
								<tr
									style={{
										background: "#F5F3EE",
										borderBottom: "0.5px solid #E6E1D6",
									}}
								>
									{[
										"",
										"Produit",
										"Catégorie",
										"Prix",
										"Stock",
										"Statut",
										"",
									].map((heading, index) => (
										<th
											key={index}
											style={{
												padding: "11px 14px",
												textAlign: "left",
												fontSize: 11,
												fontWeight: 600,
												color: "#8A857D",
												letterSpacing: "0.1em",
												textTransform: "uppercase",
											}}
										>
											{heading}
										</th>
									))}
								</tr>
							</thead>

							<tbody>
								{products.map((product, index) => (
									<tr
										key={product.id}
										style={{
											borderBottom:
												index < products.length - 1
													? "0.5px solid #F1ECE4"
													: "none",
											transition: "background 0.15s",
										}}
										onMouseEnter={(e) =>
											(e.currentTarget.style.background = "#FAF8F4")
										}
										onMouseLeave={(e) =>
											(e.currentTarget.style.background = "transparent")
										}
									>
										{/* Image */}
										<td
											style={{
												padding: "12px 14px",
											}}
										>
											<ProductThumb
												src={product.image_url}
												alt={product.name}
											/>
										</td>

										{/* Product */}
										<td
											style={{
												padding: "12px 14px",
											}}
										>
											<p
												style={{
													margin: 0,
													fontWeight: 600,
													fontSize: 14,
													color: "#2A2A2A",
												}}
											>
												{product.name}
											</p>

											<p
												style={{
													margin: "2px 0 0",
													fontSize: 12,
													color: "#B4B2A9",
													fontFamily: "monospace",
												}}
											>
												{product.slug}
											</p>
										</td>

										{/* Category */}
										<td
											style={{
												padding: "12px 14px",
											}}
										>
											<span
												style={{
													display: "inline-block",
													padding: "3px 10px",
													borderRadius: 20,
													fontSize: 12,
													fontWeight: 500,
													background: "#F1ECE4",
													color: "#6B6B6B",
												}}
											>
												{product.categories?.name || "—"}
											</span>
										</td>

										{/* Price */}
										<td
											style={{
												padding: "12px 14px",
												fontSize: 14,
												fontWeight: 600,
												color: "#2A2A2A",
											}}
										>
											{formatPrice(product.price)}
										</td>

										{/* Stock */}
										<td
											style={{
												padding: "12px 14px",
												fontSize: 14,
												color: product.stock === 0 ? "#E24B4A" : "#2A2A2A",
											}}
										>
											{product.stock ?? "—"}
										</td>

										{/* Status */}
										<td
											style={{
												padding: "12px 14px",
											}}
										>
											<StatusBadge
												available={product.available}
												stock={product.stock}
											/>
										</td>

										{/* Actions */}
										<td
											style={{
												padding: "12px 14px",
											}}
										>
											<div
												style={{
													display: "flex",
													gap: 6,
												}}
											>
												<button
													type="button"
													onClick={() => openEdit(product)}
													title="Modifier"
													style={{
														padding: "6px 10px",
														borderRadius: 8,
														border: "0.5px solid #D3D1C7",
														background: "transparent",
														color: "#6B6B6B",
														cursor: "pointer",
														fontSize: 13,
														fontFamily: "inherit",
													}}
												>
													✏️
												</button>

												<button
													type="button"
													onClick={() => remove(product.id)}
													title="Supprimer"
													disabled={deleting === product.id}
													style={{
														padding: "6px 10px",
														borderRadius: 8,
														border: "0.5px solid #F7C1C1",
														background: "transparent",
														color: "#A32D2D",
														cursor:
															deleting === product.id
																? "not-allowed"
																: "pointer",
														fontSize: 13,
														opacity: deleting === product.id ? 0.5 : 1,
														fontFamily: "inherit",
													}}
												>
													{deleting === product.id ? "..." : "🗑"}
												</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{/* Edit modal */}
			{editOpen && form && (
				<ProductEditModal
					form={form}
					onChange={onChange}
					onSubmit={save}
					onClose={close}
					submitting={saving}
					imagePreview={preview}
					onImageChange={onImage}
					categories={categories}
				/>
			)}
		</div>
	);
};

export default AdminProductsPage;
